import { useState, useEffect } from 'react';
import { fetchAdminTours, createTour, updateTour, deleteTour } from '../common/api';
import { fetchAdminTags, createTag, deleteTag } from '../common/api';

const emptyForm = { id: '', title: '', description: '', base_price: '', max_capacity: '', minibus_discount: 10, bus_discount: 20 };

function formatKRW(n) {
  return `₩${Number(n).toLocaleString('ko-KR')}`;
}

export default function Tours() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // 'add' | 'edit'
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminTours();
      setTours(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function openAdd() {
    setForm(emptyForm);
    setEditId(null);
    setModal('add');
  }

  // 태그 추가
  async function handleTagAdd() {
    if (!tagInput.trim()) return;
    const { id } = await createTag({ tour_id: editId, tag: tagInput.trim(), sort_order: tags.length });
    setTags((prev) => [...prev, { id, tag: tagInput.trim(), sort_order: tags.length }]);
    setTagInput("");
  }

  // 태그 삭제
  async function handleTagDelete(id) {
    await deleteTag(id);
    setTags((prev) => prev.filter((t) => t.id !== id));
  }

  async function openEdit(tour) {
    setForm({
      id: tour.id,
      title: tour.title,
      description: tour.description || '',
      base_price: tour.base_price,
      max_capacity: tour.max_capacity,
      minibus_discount: tour.minibus_discount ?? 10,
      bus_discount: tour.bus_discount ?? 20,
    });
    setEditId(tour.id);
    setModal('edit');
    // 태그 로드
    const data = await fetchAdminTags(tour.id);
    setTags(data);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'add') {
        await createTour({
          id: form.id,
          title: form.title,
          description: form.description,
          base_price: Number(form.base_price),
          max_capacity: Number(form.max_capacity),
        });
      } else {
        await updateTour(editId, {
          title: form.title,
          description: form.description,
          base_price: Number(form.base_price),
          max_capacity: Number(form.max_capacity),
          minibus_discount: Number(form.minibus_discount),
          bus_discount: Number(form.bus_discount),
        });
      }
      setModal(null);
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this tour?')) return;
    try {
      await deleteTour(id);
      load();
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Tour Management</h1>
          <p className="text-gray-500 text-sm mt-1">{tours.length} tours total</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-4 py-2 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            ↻ Refresh
          </button>
          <button
            onClick={openAdd}
            className="px-4 py-2 bg-[#E8876A] text-white text-sm font-medium rounded-lg hover:bg-[#D96F52] transition-colors"
          >
            + Add Tour
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          ⚠️ {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Base Price</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Discount</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Capacity</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tags</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tours.map(tour => (
                <tr key={tour.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-400 font-mono">{tour.id}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-800">{tour.title}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{formatKRW(tour.base_price)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full mr-1">Minibus {tour.minibus_discount}%</span>
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">Bus {tour.bus_discount}%</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 text-left">
                    {tour.max_capacity}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(tour.tags ?? []).map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-[#E8876A]/10 text-[#E8876A] text-[11px] rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-3">
                      <button onClick={() => openEdit(tour)} className="text-xs text-blue-600 hover:underline">Edit</button>
                      <button onClick={() => handleDelete(tour.id)} className="text-xs text-red-500 hover:underline">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && tours.length === 0 && (
          <div className="py-12 text-center text-gray-400 text-sm">No tours found.</div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">
              {modal === 'add' ? 'Add Tour' : 'Edit Tour'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* ID — 추가 시만 */}
              {modal === 'add' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tour ID <span className="text-gray-400 font-normal">(영문 소문자, 언더스코어)</span>
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A] font-mono"
                    placeholder="ex) nami_daytrip"
                    value={form.id}
                    onChange={e => setForm({ ...form, id: e.target.value })}
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A] resize-none"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Price (₩)</label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                    value={form.base_price}
                    onChange={e => setForm({ ...form, base_price: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Capacity</label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                    value={form.max_capacity}
                    onChange={e => setForm({ ...form, max_capacity: e.target.value })}
                    required
                  />
                </div>
                {/* 태그 — Edit 모드에서만 */}
                {modal === 'edit' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {tags.map((t) => (
                        <span key={t.id} className="flex items-center gap-1 px-2.5 py-1 bg-[#E8876A]/10 text-[#E8876A] text-xs rounded-full">
                          {t.tag}
                          <button
                            type="button"
                            onClick={() => handleTagDelete(t.id)}
                            className="text-[#E8876A] hover:text-red-500 ml-0.5"
                          >×</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                        placeholder="태그 입력 후 Add"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleTagAdd())}
                      />
                      <button
                        type="button"
                        onClick={handleTagAdd}
                        className="px-3 py-2 bg-[#E8876A] text-white text-sm rounded-lg hover:bg-[#D96F52]"
                      >Add</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minibus Discount (%)
                  </label>
                  <input
                    type="number"
                    min={0} max={100}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                    value={form.minibus_discount}
                    onChange={e => setForm({ ...form, minibus_discount: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bus Discount (%)
                  </label>
                  <input
                    type="number"
                    min={0} max={100}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                    value={form.bus_discount}
                    onChange={e => setForm({ ...form, bus_discount: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setModal(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-[#E8876A] text-white text-sm rounded-lg hover:bg-[#D96F52] disabled:opacity-50"
                >
                  {saving ? 'Saving...' : modal === 'add' ? 'Add' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}